import { useAccessoireCheckoutRequest } from "app/hooks/checkout/useAccessoireCheckoutRequest";
import { useCreateStandardOrderBumbalDate } from "app/hooks/checkout/useCreateStandardOrderBumbalDate";
import { useStandardProductCreatePaymentLink } from "app/hooks/payment/useStandardProductCreatePaymentLink";
import { useAnalytics } from "app/hooks/shared/analytics/useAnalytics";
import { useStandardProductType } from "app/hooks/shared/standard-product/useStandardProductType";
import { useVoucherOrderAddition } from "app/hooks/shared/useVoucherOrderAddition";
import { useCartContext } from "app/providers/CartProvider";
import {
  CheckoutTabIds,
  useCheckoutTabsContext,
} from "pages/checkout/providers/CheckoutTabsProvider";
import { usePaymentContext } from "pages/payment/providers/PaymentProvider";
import { toast } from "react-toastify";
import { STORAGE_ORDER_KEY } from "shared/config";
import Button, { ButtonArrowTypes, ButtonTypes } from "shared/ui/button";
import { concatClassnames } from "shared/utils/concatClassnames";
import { idealId } from "shared/utils/payment";
import { decryptVoucher } from "shared/utils/voucher";
import VoucherModal from "widgets/shared/add-voucher-modal/VoucherModal";
import VoucherModalOrder from "widgets/shared/add-voucher-modal/VoucherModalOrder";

const CheckoutPaymentFooter = () => {
  const cartContext = useCartContext();
  const cart = cartContext.getCart();
  const { updateCurrentTab } = useCheckoutTabsContext();

  const {
    createStandardProductOrderPaymentLink,
    isCreateStandardOrderPaymentLinkLoading,
  } = useStandardProductCreatePaymentLink();
  const createBumbalDateMutation = useCreateStandardOrderBumbalDate();

  // Get the order ID and order type from the search parameters
  const orderID = localStorage.getItem(STORAGE_ORDER_KEY);
  const orderType = cart.standardProducts.length > 0 ? "p" : "d";
  const orderModel = orderType === "d" ? "order" : "standardOrder";

  // Voucher helpers
  const { addVoucherToOrder, status } = useVoucherOrderAddition({
    orderID,
    orderType: orderType,
    toastEnabled: false,
  });

  const { getPaymentTypeList, paymentAction, paymentMethod, bank } =
    usePaymentContext();
  const { onAddStandardProductPaymentInfo } = useAnalytics();
  const { mutateAccesoireCheckout, isCheckoutRequestLoading } =
    useAccessoireCheckoutRequest(paymentMethod, bank);
  const canProceed = () => {
    if (!cart.standardProductPayment) return false;
    if (!paymentMethod) return false;
    if (paymentMethod === idealId && !bank) return false;
    return true;
  };
  const productAnalysis = useStandardProductType(cart);

  const onAccessoireProceedClick = () => mutateAccesoireCheckout();

  const onAccousticPanelProceedClick = async () => {
    try {
      // Get the order ID from local storage
      const orderId = localStorage.getItem(STORAGE_ORDER_KEY);

      // If the order ID is not found, display an error message and return
      if (!orderId) {
        toast.error(
          "Er is iets misgegaan met uw betaling. Neem voor meer informatie contact op met klantenservice.",
        );
        return;
      }

      // If the cart is in bumbalFlow, create a bumbal date mutation
      // if (cart.bumbalFlow) {
      //   createBumbalDateMutation.mutateAsync({
      //     orderId,
      //     type: cart.bumbalFlow || "",
      //     selectedDate: new Date(cart.delivery.date),
      //   });
      // }
      if (cart.discountCode) {
        try {
          // Assign voucher to order
          await addVoucherToOrder(decryptVoucher(cart.discountCode));
        } catch (error) {
          toast.error("Er is een fout opgetreden. Probeer het opnieuw.");
        }
      }

      // Create the payment link payload
      const paymentLinkPayload = {
        paymentType: "direct",
        paymentMethod: parseInt(paymentMethod),
        paymentBank: parseInt(bank),
      };

      // Create the standard product order payment link
      createStandardProductOrderPaymentLink({
        orderId: orderId,
        payload: paymentLinkPayload,
      });

      // Submit analytics event
      const action = getPaymentTypeList([]).find(
        (type) => type.action === paymentAction,
      );
      const method = action?.methods.find(
        (method) => method.id === paymentMethod,
      );
      const paymentBank = method?.banks?.find((b) => b.id === bank);

      onAddStandardProductPaymentInfo(cart, {
        action: action?.action || "-",
        method: method?.visibleName || "-",
        bank: paymentBank?.visibleName || "-",
      });
    } catch (error) {
      // Handle any errors
      toast.error("An error occurred. Please try again later.");
    }
  };

  /* Afspraak Inplannen is TEMPORARY DISABLED FOR THE LAUNCH */
  /* TODO: revert to going to APPOINTMENT step */
  const onGoToPrevTabClick = () => {
    updateCurrentTab(CheckoutTabIds.CALENDAR);
  };

  const isCalendarCheckoutFlow =
    productAnalysis.someAkoestischepanelen ||
    productAnalysis.someAccessoireAkoestischPaneel;

  const disableButtonOnRequest =
    !canProceed() ||
    isCreateStandardOrderPaymentLinkLoading ||
    createBumbalDateMutation.isLoading;

  return (
    <>
      <footer
        className={concatClassnames(
          "gg-px-4 sm:gg-px-14 md:gg-px-14 lg:gg-px-14 xl:gg-px-14 2xl:gg-px-32",
          "gg-bg-dark-blue gg-py-11 gg-text-white",
        )}
      >
        <div className="gg-grid gg-w-full gg-items-center gg-gap-y-9 lg:gg-flex lg:gg-justify-between lg:gg-gap-y-0">
          {!!orderID && orderType !== "p" ? (
            <VoucherModalOrder orderID={orderID} orderType={orderType} />
          ) : (
            <VoucherModal orderType={orderModel} />
          )}

          <div className="gg-grid gg-w-full gg-gap-y-6 gg-text-lg gg-font-bold sm:gg-flex sm:gg-gap-x-5 md:gg-gap-x-8 md:gg-gap-y-0 lg:gg-justify-end">
            {/* Afspraak Inplannen is TEMPORARY DISABLED FOR THE LAUNCH */
            /* TODO: revert this button's text back to 'Terug naar afspaak inplannen' */}
            <Button
              onClick={() => onGoToPrevTabClick()}
              type={ButtonTypes.WHITE}
              arrow={ButtonArrowTypes.LEFT}
              className="gg-flex gg-w-full gg-items-center gg-justify-center gg-justify-self-end gg-px-5 gg-py-3 gg-text-base lg:gg-w-fit lg:gg-max-w-xs xl:gg-py-3.5"
            >
              {isCalendarCheckoutFlow
                ? "Terug naar agenda"
                : "Terug naar jouw gegevens"}
            </Button>

            {isCalendarCheckoutFlow ? (
              <Button
                type={ButtonTypes.YELLOW}
                arrow={ButtonArrowTypes.RIGHT}
                onClick={() => onAccousticPanelProceedClick()}
                className="gg-flex gg-w-full gg-items-center gg-justify-center gg-justify-self-end gg-px-5 gg-py-3 gg-text-base lg:gg-w-fit lg:gg-max-w-xs lg:gg-px-12 xl:gg-py-3.5"
                disabled={disableButtonOnRequest}
                loading={
                  isCreateStandardOrderPaymentLinkLoading ||
                  createBumbalDateMutation.isLoading ||
                  status === "loading"
                }
              >
                Betalen
              </Button>
            ) : (
              <Button
                type={ButtonTypes.YELLOW}
                arrow={ButtonArrowTypes.RIGHT}
                onClick={() => onAccessoireProceedClick()}
                className="gg-flex gg-w-full gg-items-center gg-justify-center gg-justify-self-end gg-px-5 gg-py-3 gg-text-base lg:gg-w-fit lg:gg-max-w-xs lg:gg-px-12 xl:gg-py-3.5"
                disabled={!canProceed()}
                loading={isCheckoutRequestLoading}
              >
                Betalen
              </Button>
            )}
          </div>
        </div>
      </footer>
    </>
  );
};
export default CheckoutPaymentFooter;
